import React from "react"

import styles from './RegularExpressions.module.scss'
import globalStyles from '../../../../App.module.scss'
import asterix from './images/asterix.png'
import bracketRanges from './images/bracket_ranges.png'
import brackets from './images/brackets.png'
import bracketsCaret from './images/brackets_caret.png'
import caret from './images/caret.png'
import curlyBraces from './images/curly_braces.png'
import dolar from './images/dolar.png'
import escape from './images/escape.png'
import parentheses from './images/parentheses.png'
import period from './images/period.png'
import pipe from './images/pipe.png'
import plusSign from './images/plus_sign.png'
import questionMark from './images/question_mark.png'

export default function RegularExpressions() {
    return (
        <div className={styles.regularExpressions}>
            <h1>Regular expressions</h1>
            <p>There isn't just one set of regular expressions. Different applications use different types of <em>regular
                expression engines</em>. These are the
                two most commong regular expression engines:</p>
            <ul>
                <li>The POSIX <b>Basic Regular Expression</b> (BRE) engine</li>
                <li>The POSIX <b>Extended Regular Expression</b> (ERE) engine</li>
            </ul>
            <p>The special characters recognized by regular expressions are < b>{"^ $ [] \\ . * { } + ? | () "}< /b></p>
            <div>
                <h2>Basic Regular Expressions</h2>
                <h4>{"^"}</h4>
                <p>The <b>caret</b> symbol will match the patern or character searched at the begining of each
                    line.<br/>Example:
                </p>
                <div style={{maxWidth: '402px', maxHeight: '122px'}}>
                    <img src={caret} alt={""}/>
                </div>
                <h4>{"$"}</h4>
                <p>The <b>dolar</b> sign will match the patern or character searched at the end of each line.<br/>Example:
                </p>
                <div style={{maxWidth: '401px', maxHeight: '121px'}}>
                    <img src={dolar} alt={""}/>
                </div>
                <h4>{"[ ]"}</h4>
                <p><b>Brackets</b> can be used to specify a set of characters like <b>[ioe]</b>. Grep will find all the
                    lines
                    that contain at least one of the specified characters inside the brackets.<br/>Example:</p>

                <div style={{maxWidth: '401px', maxHeight: '134px'}}>
                    <img src={brackets} alt={""}/>
                </div>

                <p>The caret symbol inside brackets <strong>{"[^]"}</strong> will invert the selection. Grep will match
                    all
                    the
                    lines that <strong>DO NOT</strong> contain the specified characters.<br/>Example:</p>
                <div style={{maxWidth: '402px', maxHeight: '122px'}}>
                    <img src={bracketsCaret} alt={""}/>
                </div>
                <p>You can use a <b>range of characters</b> within brackets like <b>[a-zAZ]</b> or numbers
                    like <b>[0-9]</b>. <br/> Example:</p>
                <div style={{maxWidth: '402px', maxHeight: '134px'}}>
                    <img src={bracketRanges} alt={""}/>
                </div>
                <h4>{"\\"}</h4>
                <p><b>Back slash</b> is the escape character. It used when the searced pattern includes special
                    characters.<br/>Example:
                </p>
                <div style={{maxWidth: '402px', maxHeight: '134px'}}>
                    <img src={escape} alt={""}/>
                </div>
                <h4 style={{fontSize: '20px'}}>.</h4>
                <p>The <b>period</b> is used to match any character in the place where it is used.<br/>Example:</p>
                <div style={{maxWidth: '402px', maxHeight: '134px'}}>
                    <img src={period} alt={""}/>
                </div>
                <h4 style={{fontSize: '20px'}}>*</h4>
                <p>The <b>asterix</b> can be a little bit tricky. <b>It is NOT a wildcard</b>, it will match zero or
                    more
                    times
                    the preceding character.
                    <br/>Example:</p>
                <div style={{maxWidth: '402px', maxHeight: '134px'}}>
                    <img src={asterix} alt={""}/>
                </div>

                <h2>Character classes</h2>
                <p>It is possible to use ranges like <b>[0-9]</b> or a class like <b>[[:alpha:]]</b> to match for only
                    numbers.
                    The <abbr title="Basic Regular Expressions">BRE</abbr> contains special character classes that can
                    be
                    used
                    to match specific types of characters.</p>
                <div className={globalStyles.styledTable}>
                    <table>
                        <thead>
                        <tr>
                            <th className="table_black_hd" colSpan="2">BRE Special Character Classes.</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td><strong>[[:alpha:]]</strong></td>
                            <td>Match any alphabetical character, either upper or lower case.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:alnum:]]</strong></td>
                            <td>Match any alphanumeric character 0 - 9, A - Z, or a - z.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:blank:]]</strong></td>
                            <td>Match a space or Tab character.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:digit:]]</strong></td>
                            <td>Match a numerical digit from 0 through 9.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:lower:]]</strong></td>
                            <td>Match any lower-case alphabetical character a - z.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:print:]]</strong></td>
                            <td>Match any printable character.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:punct:]]</strong></td>
                            <td>Match a punctuation character.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:space:]]</strong></td>
                            <td>Match any whitespace character: space, Tab, NL, FF, VT, CR.</td>
                        </tr>
                        <tr>
                            <td><strong>[[:upper:]]</strong></td>
                            <td>Match any upper-case alphabetical character A - Z.</td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <h2>Extended Regular Expressions</h2>
                <h4>?</h4>
                <p>The <b>question mark</b> indicates that the preceding character can appear zero or one time.
                    The difference with the asterix is that the question mark will not match repeating occurrences of
                    the
                    character. <br/>Example:</p>
                <div style={{maxWidth: '402px', maxHeight: '122px'}}>
                    <img src={questionMark} alt={""}/>
                </div>
                <h4>+</h4>
                <p>The <b>plus sign</b> indicates that the preceding character can appear one or more times, but must be
                    present
                    at least once.</p>
                <div style={{maxWidth: '402px', maxHeight: '168px'}}>
                    <img src={plusSign} alt={""}/>
                </div>
                <h4>{"{}"}</h4>
                <p><b>Curly
                    braces</b>{" allow to spedify a limit on a repeatable expression,{lower,upper} orjust {min}."}<br/>Example:
                </p>
                <div style={{maxWidth: '402px', maxHeight: '198px'}}>
                    <img src={curlyBraces} alt={""}/>
                </div>

                <h4>|</h4>
                <p>The <b>pipe symbol</b> is used to match two or more patterns treated as a <b>logical OR</b> formula.
                </p>
                <div style={{maxWidth: '402px', maxHeight: '134px'}}>
                    <img src={pipe} alt={""}/>
                </div>

                <h4>( )</h4>
                <p><b>Parentheses</b> are used to group regular expressions patterns, the resulting group will be
                    treated as
                    a
                    character.
                    <br/> Example:</p>
                <div style={{maxWidth: '402px', maxHeight: '148px'}}>
                    <img src={parentheses} alt={""}/>
                </div>
                <h2>Examples</h2>
                <p>Find all blanc lines in a file:</p>
                <pre className={globalStyles.code}>{"grep '^$' {file}"}</pre>
                <p>Find any two digit numbers in a file:</p>
                <pre className={globalStyles.code}>{"grep ^[0-9][0-9]$ {file}"}</pre>
                <p>Find any line that starts with a period:</p>
                <pre className={globalStyles.code}>{"grep '^\\.' {file}"}</pre>
                <p>Find lines that only have one character:</p>
                <pre className={globalStyles.code}>{"grep '^.$' {file}"}</pre>
            </div>
        </div>)
}